import { Message, Suggestion } from "../types";

type GreetingSuggestionTemplate = {
  translationKey: string;
  fallbackText: string;
  action: string;
  category: string;
  categoryTranslationKey: string;
  categoryFallbackText: string;
};

export const GREETING_SUGGESTION_TEMPLATES: GreetingSuggestionTemplate[] = [
  {
    translationKey: "chatbot-remove-members",
    fallbackText: "Remover colaboradores",
    action: "action_do_for_me",
    category: "members",
    categoryTranslationKey: "chatbot-category-members",
    categoryFallbackText: "Colaboradores",
  },
  {
    translationKey: "chatbot-add-members",
    fallbackText: "Adicionar colaboradores",
    action: "action_do_for_me_invite",
    category: "members",
    categoryTranslationKey: "chatbot-category-members",
    categoryFallbackText: "Colaboradores",
  },
  {
    translationKey: "chatbot-change-cost-center",
    fallbackText: "Alterar centro de custo",
    action: "action_do_for_me_cost_center",
    category: "members",
    categoryTranslationKey: "chatbot-category-members",
    categoryFallbackText: "Colaboradores",
  },
  {
    translationKey: "chatbot-change-member-plan",
    fallbackText: "Alterar plano de colaboradores",
    action: "action_do_for_me_update_plan",
    category: "members",
    categoryTranslationKey: "chatbot-category-members",
    categoryFallbackText: "Colaboradores",
  },
  {
    translationKey: "chatbot-suggestion-booking",
    fallbackText: "Reservas",
    action: "reservas_realizar",
    category: "espacos-reservas",
    categoryTranslationKey: "chatbot-category-spaces-reservations",
    categoryFallbackText: "Espaços e reservas",
  },
  {
    translationKey: "chatbot-suggestion-checkin-usage",
    fallbackText: "Check-in e utilização",
    action: "checkin_realizar",
    category: "espacos-reservas",
    categoryTranslationKey: "chatbot-category-spaces-reservations",
    categoryFallbackText: "Espaços e reservas",
  },

  {
    translationKey: "chatbot-suggestion-import-members",
    fallbackText: "Importação de membros",
    action: "importacao_membros",
    category: "gestao-relatorios",
    categoryTranslationKey: "chatbot-category-management-reports",
    categoryFallbackText: "Gestão e relatórios",
  },
  {
    translationKey: "chatbot-shortcut-usage-reasons-insights",
    fallbackText: "Motivos de uso das reservas",
    action: "motivos_de_uso_insights",
    category: "gestao-relatorios",
    categoryTranslationKey: "chatbot-category-management-reports",
    categoryFallbackText: "Gestão e relatórios",
  },
];

export interface CategorizedSuggestions {
  category: string;
  categoryLabel: string;
  suggestions: Suggestion[];
}

export function buildGreetingSuggestions(
  translate: (key: string) => string | undefined,
): Suggestion[] {
  return GREETING_SUGGESTION_TEMPLATES.map((template) => {
    const translated = translate(template.translationKey);
    const text =
      typeof translated === "string" && translated.length > 0
        ? translated
        : template.fallbackText;
    return {
      text,
      action: template.action,
      category: template.category,
    };
  });
}

export function buildCategorizedGreetingSuggestions(
  translate: (key: string) => string | undefined,
): CategorizedSuggestions[] {
  const categorized: Record<string, CategorizedSuggestions> = {};

  GREETING_SUGGESTION_TEMPLATES.forEach((template) => {
    const translated = translate(template.translationKey);
    const text =
      typeof translated === "string" && translated.length > 0
        ? translated
        : template.fallbackText;

    const categoryTranslated = translate(template.categoryTranslationKey);
    const categoryLabel =
      typeof categoryTranslated === "string" && categoryTranslated.length > 0
        ? categoryTranslated
        : template.categoryFallbackText;

    if (!categorized[template.category]) {
      categorized[template.category] = {
        category: template.category,
        categoryLabel,
        suggestions: [],
      };
    }

    categorized[template.category].suggestions.push({
      text,
      action: template.action,
      category: template.category,
    });
  });

  return Object.values(categorized);
}

/**
 * Retorna o `fallbackText` (texto em pt-BR) para um `action` conhecido, sem
 * precisar de contexto de i18n. Útil em camadas de serviço que transformam
 * dados da API antes de chegarem ao React (ex.: `mapMessage` no histórico).
 *
 * Retorna `undefined` quando a action não está mapeada nos templates.
 */
export function resolveActionFallback(action: string): string | undefined {
  return GREETING_SUGGESTION_TEMPLATES.find((t) => t.action === action)
    ?.fallbackText;
}

/**
 * Resolve o label legível para um `action` a partir dos templates de greeting.
 *
 * Quando o backend emite uma suggestion cujo `text` é a chave interna da action
 * (ex.: `"motivos_de_uso_insights"`), este helper devolve o texto traduzido ou
 * o `fallbackText` do template correspondente, evitando que a bolha do usuário
 * exiba o nome bruto da variável.
 *
 * Retorna `undefined` quando a action não está mapeada nos templates — nesse caso
 * o chamador deve usar `suggestion.text` como fallback.
 */
export function resolveDisplayTextForAction(
  action: string,
  translate: (key: string) => string | undefined,
): string | undefined {
  const template = GREETING_SUGGESTION_TEMPLATES.find(
    (t) => t.action === action,
  );
  if (!template) return undefined;
  const translated = translate(template.translationKey);
  // i18next devolve a própria chave quando a tradução não existe
  const isKeyReturn = !translated || translated === template.translationKey;
  return isKeyReturn ? template.fallbackText : (translated as string);
}

export const GREETING: Message = {
  id: "greeting-1",
  content: "",
  isUser: false,
  timestamp: new Date(),
  suggestions: [],
};

/**
 * Registry de suggestions cuja ação é executada no próprio front,
 * em vez de enviar uma mensagem para o agente.
 *
 * Convenção (acordada com o agente em `intent_router.py`):
 *   - Esses action IDs nunca são roteados para nodes do agente
 *   - O agente os emite como `Suggestion.action` em `related_topics` ou inline
 *   - O front intercepta no `handleSuggestionClick` e executa o handler
 *
 * Features registram seus handlers via `registerClientSuggestionHandler`
 * quando montam (ex.: `compare-offices-button`) e desregistram no unmount.
 */
type ClientSuggestionHandler = () => void;

// Action IDs do fluxo de comparação de escritórios — compartilhados entre
// compare-offices-button (que registra os handlers) e suggestion-cards (que
// desabilita/renderiza os botões enquanto o upload está em andamento).
export const ACTION_DOWNLOAD_TEMPLATE = "action_download_template";
export const ACTION_UPLOAD_PORTFOLIO = "action_upload_portfolio";

const CLIENT_SUGGESTION_REGISTRY: Record<string, ClientSuggestionHandler> = {};

export function registerClientSuggestionHandler(
  action: string,
  handler: ClientSuggestionHandler,
): void {
  CLIENT_SUGGESTION_REGISTRY[action] = handler;
}

export function unregisterClientSuggestionHandler(action: string): void {
  delete CLIENT_SUGGESTION_REGISTRY[action];
}

export function getClientSuggestionHandler(
  action: string | undefined,
): ClientSuggestionHandler | undefined {
  if (!action) return undefined;
  return CLIENT_SUGGESTION_REGISTRY[action];
}

/** Estado de upload do comparador de escritórios (suggestion "Enviar planilha"). */
type ComparisonUploadLoadingListener = (loading: boolean) => void;

let comparisonUploadLoading = false;
const comparisonUploadLoadingListeners =
  new Set<ComparisonUploadLoadingListener>();

export function setComparisonUploadLoading(loading: boolean): void {
  if (comparisonUploadLoading === loading) return;
  comparisonUploadLoading = loading;
  comparisonUploadLoadingListeners.forEach((listener) => listener(loading));
}

export function subscribeComparisonUploadLoading(
  listener: ComparisonUploadLoadingListener,
): () => void {
  comparisonUploadLoadingListeners.add(listener);
  listener(comparisonUploadLoading);
  return () => {
    comparisonUploadLoadingListeners.delete(listener);
  };
}

function stripCodeFences(s: string) {
  return s
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/```$/i, "");
}

function tryParseJSON<T = any>(s: string): T | null {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function convertChartJsToChartSpec(chartJsData: any): any | null {
  if (!chartJsData || typeof chartJsData !== "object") {
    return null;
  }

  if (chartJsData.x && chartJsData.series) {
    return chartJsData;
  }

  if (
    chartJsData.data &&
    chartJsData.data.labels &&
    chartJsData.data.datasets
  ) {
    const { labels, datasets } = chartJsData.data;
    const type = chartJsData.type || "bar";

    const series = datasets.map((dataset: any, index: number) => ({
      name: dataset.label || `Série ${index + 1}`,
      data: dataset.data || [],
      unit: dataset.unit,
    }));

    const title =
      chartJsData.options?.plugins?.title?.text ||
      chartJsData.title ||
      "Gráfico";

    const isHorizontal =
      type === "horizontalBar" || chartJsData.options?.indexAxis === "y";

    return {
      type: isHorizontal ? "bar" : type,
      title,
      x: labels,
      series,
      note: chartJsData.note,
      stacked: chartJsData.options?.scales?.x?.stacked || false,
      horizontal: isHorizontal,
    };
  }

  return null;
}
export function extractAgentPayload(raw: string | any) {
  if (raw && typeof raw === "object") {
    if (typeof raw.response === "string") {
      const cleaned = stripCodeFences(raw.response);
      const parsed = tryParseJSON<any>(cleaned);

      if (parsed && typeof parsed === "object") {
        let visualization = parsed.visualization ?? null;
        if (visualization && typeof visualization === "object") {
          const converted = convertChartJsToChartSpec(visualization);
          if (converted) {
            visualization = converted;
          }
        }

        return {
          reply: parsed.reply ?? raw.response,
          visualization,
          suggestions: parsed.suggestions ?? raw.suggestions ?? undefined,
        };
      }

      return {
        reply: raw.response,
        visualization: raw.visualization ?? null,
        suggestions: raw.suggestions ?? undefined,
      };
    }

    if ("reply" in raw || "visualization" in raw || "suggestions" in raw) {
      return {
        reply: raw.reply ?? "",
        visualization: raw.visualization ?? null,
        suggestions: raw.suggestions ?? undefined,
      };
    }

    if (raw.response && typeof raw.response === "object") {
      let visualization =
        raw.response.visualization ?? raw.visualization ?? null;
      if (visualization && typeof visualization === "object") {
        const converted = convertChartJsToChartSpec(visualization);
        if (converted) {
          visualization = converted;
        }
      }

      return {
        reply: raw.response.reply ?? raw.response.response ?? "",
        visualization,
        suggestions: raw.response.suggestions ?? raw.suggestions ?? undefined,
      };
    }
  }

  if (typeof raw === "string") {
    const cleaned = stripCodeFences(raw);
    let obj = tryParseJSON<any>(cleaned);

    if (
      obj &&
      typeof obj === "object" &&
      ("reply" in obj || "visualization" in obj || "suggestions" in obj)
    ) {
      let visualization = obj.visualization ?? null;
      if (visualization && typeof visualization === "object") {
        const converted = convertChartJsToChartSpec(visualization);
        if (converted) {
          visualization = converted;
        }
      }

      return {
        reply: obj.reply ?? "",
        visualization,
        suggestions: obj.suggestions ?? undefined,
      };
    }

    if (obj && typeof obj === "object" && typeof obj.response === "string") {
      const innerClean = stripCodeFences(obj.response);
      const inner = tryParseJSON<any>(innerClean);
      if (inner && ("reply" in inner || "visualization" in inner)) {
        let visualization = inner.visualization ?? null;
        if (visualization && typeof visualization === "object") {
          const converted = convertChartJsToChartSpec(visualization);
          if (converted) {
            visualization = converted;
          }
        }

        return {
          reply: inner.reply ?? "",
          visualization,
          suggestions: inner.suggestions ?? obj.suggestions ?? undefined,
        };
      }
      return {
        reply: obj.response,
        visualization: null,
        suggestions: obj.suggestions ?? undefined,
      };
    }

    if (obj && typeof obj === "object") {
      const legacyText = obj.response ?? obj.answer ?? obj.message;
      if (typeof legacyText === "string") {
        return {
          reply: legacyText,
          visualization: null,
          suggestions: obj.suggestions ?? undefined,
        };
      }
    }

    if (!obj && cleaned) {
      return { reply: cleaned, visualization: null };
    }
  }

  return null;
}
